import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import ImageInput from "../../common/ImageInput";
import FilePreview from "../../FilePreview/FilePreview";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as attractionCategoryService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";

const slugifyCategoryName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const AttractionCategory = () => {
  const queryClient = useQueryClient();

  // State management
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState(1);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [errors, setErrors] = useState({ title: "", image: "" });
  const [isLoading, setIsLoading] = useState(false);

  // Image state
  const [categoryImage, setCategoryImage] = useState(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Query
  const { data: categoryData } = useQuery({
    queryKey: ["attractionCategoriesAdmin", currentPage, activeSearch],
    queryFn: () =>
      attractionCategoryService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: attractionCategoryService.create,
    onSuccess: () => {
      setIsLoading(false);
      showSuccessMessage("Attraction category added successfully!");
      setAddDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["attractionCategoriesAdmin"]);
      queryClient.invalidateQueries(["attractionCategories"]); // also refresh supplier dropdown
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(
        error.response?.data?.message || "Failed to add attraction category"
      );
      setShowError(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: attractionCategoryService.update,
    onSuccess: () => {
      setIsLoading(false);
      showSuccessMessage("Attraction category updated successfully!");
      setEditDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["attractionCategoriesAdmin"]);
      queryClient.invalidateQueries(["attractionCategories"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(
        error.response?.data?.message || "Failed to update attraction category"
      );
      setShowError(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: attractionCategoryService.deleteAttractionCategory,
    onSuccess: () => {
      setIsLoading(false);
      showSuccessMessage("Attraction category deleted successfully!");
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["attractionCategoriesAdmin"]);
      queryClient.invalidateQueries(["attractionCategories"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(
        error.response?.data?.message || "Failed to delete attraction category"
      );
      setShowError(true);
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: attractionCategoryService.getById,
    onSuccess: (data) => {
      setIsLoading(false);
      const cat = data?.data || data;
      setSelectedCategory(cat);
      setTitle(cat.name || cat.title || "");
      setStatus(cat.status ?? 1);
      setCategoryImage(null);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.message || "Failed to fetch category details");
    },
  });

  // Helpers
  const resetForm = () => {
    setTitle("");
    setStatus(1);
    setCategoryImage(null);
    setErrors({ title: "", image: "" });
  };

  const validateForm = (isNew = false) => {
    let tempErrors = { title: "", image: "" };
    let isValid = true;

    if (!title.trim()) {
      tempErrors.title = "Title is required";
      isValid = false;
    }

    if (isNew && !categoryImage) {
      tempErrors.image = "Image is required for new categories";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (msg) => {
    setErrorMessage(msg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Handlers
  const handleAddClick = () => {
    resetForm();
    setSelectedCategory(null);
    setAddDialogOpen(true);
  };

  const handleEditClick = (id) => {
    setIsLoading(true);
    getByIdMutation.mutate(id);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleSave = () => {
    setIsLoading(true);
    if (validateForm(true)) {
      const formData = new FormData();
      formData.append("name", title);
      formData.append("slug", slugifyCategoryName(title));
      formData.append("status", Number(status));
      if (categoryImage) formData.append("image", categoryImage);
      createMutation.mutate(formData);
    } else {
      setIsLoading(false);
    }
  };

  const handleUpdate = () => {
    setIsLoading(true);
    if (validateForm(false)) {
      const formData = new FormData();
      formData.append("name", title);
      formData.append("slug", slugifyCategoryName(title));
      formData.append("status", Number(status));
      if (categoryImage) formData.append("image", categoryImage);
      updateMutation.mutate({ id: selectedCategory.id, data: formData });
    } else {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    if (selectedCategory) {
      setIsLoading(true);
      deleteMutation.mutate(selectedCategory.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Derive list + pagination from response
  const rawData = categoryData?.data;
  const categories = Array.isArray(rawData?.data)
    ? rawData.data
    : Array.isArray(rawData)
    ? rawData
    : [];

  const pagination = {
    currentPage: rawData?.current_page || 1,
    lastPage: rawData?.last_page || 1,
    perPage: rawData?.per_page || 10,
    total: rawData?.total || 0,
    from: rawData?.from || 0,
    to: rawData?.to || 0,
  };

  return (
    <AdminPageLayout>
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.attractionCategory.title}
        subtitle={ADMIN_PAGE_META.attractionCategory.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              placeholder="Search categories..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add Attraction Category
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Image</AdminTableTh>
          <AdminTableTh>Name</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh>Created</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {categories.length > 0 ? (
            categories.map((cat, index) => (
              <AdminTableRow key={cat.id}>
                <AdminTableCell>
                  {(pagination.currentPage - 1) * pagination.perPage + index + 1}
                </AdminTableCell>
                <AdminTableCell>
                  {cat.image ? (
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="h-8 w-8 object-cover rounded-md"
                    />
                  ) : (
                    <div className="h-8 w-8 bg-gray-200 rounded-md flex items-center justify-center">
                      <i className="fi fi-rr-picture text-gray-400 text-xs"></i>
                    </div>
                  )}
                </AdminTableCell>
                <AdminTableCell>{cat.name || cat.title}</AdminTableCell>
                <AdminTableCell>
                  <StatusBadge variant={getStatusVariant(cat.status)}>
                    {cat.status === 1 ? "Active" : "Inactive"}
                  </StatusBadge>
                </AdminTableCell>
                <AdminTableCell className="text-gray-500">
                  {formatListingDateTime(cat.created_at)}
                </AdminTableCell>
                <TableActionsCell>
                  <EditActionButton
                    showLabel={false}
                    onClick={() => handleEditClick(cat.id)}
                  />
                  <DeleteActionButton
                    showLabel={false}
                    onClick={() => handleDeleteClick(cat)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={6}
              emptyMessage="No attraction categories found"
              searchQuery={activeSearch}
              onClearSearch={clearSearch}
            />
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); resetForm(); }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Attraction Category
          </div>
        }
        primaryButtonText="Add Category"
        primaryButtonAction={handleSave}
        closeButtonText="Cancel"
        closeButtonAction={() => { setAddDialogOpen(false); resetForm(); }}
        isLoading={isLoading}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`text-sm w-full px-3 py-1 border ${
              errors.title ? "border-red-500" : "border-gray-300"
            } rounded-lg focus:outline-none focus:border-primary`}
            placeholder="Enter category name"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors((prev) => ({ ...prev, title: "" }));
            }}
          />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image <span className="text-red-500">*</span>
          </label>
          <ImageInput
            onChange={(file) => {
              setCategoryImage(file);
              setErrors((prev) => ({ ...prev, image: "" }));
            }}
            label="Upload Image"
            required={true}
            accept="image/*"
          />
          {errors.image && <p className="text-red-500 text-xs mt-1">{errors.image}</p>}
          {categoryImage && (
            <div className="mt-2">
              <FilePreview
                file={categoryImage}
                label="Preview"
                onDelete={() => setCategoryImage(null)}
                isImage={true}
                showDelete={true}
              />
            </div>
          )}
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); resetForm(); }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Attraction Category
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdate}
        closeButtonText="Cancel"
        closeButtonAction={() => { setEditDialogOpen(false); resetForm(); }}
        isLoading={isLoading}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`text-sm w-full px-3 py-1 border ${
              errors.title ? "border-red-500" : "border-gray-300"
            } rounded-lg focus:outline-none focus:border-primary`}
            placeholder="Enter category name"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors((prev) => ({ ...prev, title: "" }));
            }}
          />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
          <ImageInput
            onChange={(file) => setCategoryImage(file)}
            label="Upload New Image"
            accept="image/*"
          />
          {categoryImage ? (
            <div className="mt-2">
              <FilePreview
                file={categoryImage}
                label="New Image Preview"
                onDelete={() => setCategoryImage(null)}
                isImage={true}
                showDelete={true}
              />
            </div>
          ) : selectedCategory?.image ? (
            <div className="mt-2">
              <FilePreview
                fileUrl={selectedCategory.image}
                label="Current Image"
                isImage={true}
                showDelete={false}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-2">No image uploaded</p>
          )}
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Attraction Category
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDelete}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete <strong>{selectedCategory?.name}</strong>?
            This action cannot be undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default AttractionCategory;
